import React, { useState } from "react";
import { toast } from "react-toastify";

import { createCounterpartyApi, updateCounterpartyApi } from "../api/financeiroApi";
import type { FinCounterparty } from "../api/finTypes";
import "../../dp/pages/Dp.css";

type Props = {
  token: string;
  /** Ausente: cadastro novo. */
  counterparty?: FinCounterparty | null;
  onClose: () => void;
  onSaved: (counterparty: FinCounterparty) => void;
};

const onlyDigits = (v: string) => v.replace(/\D/g, "");

/** Máscara de CPF (até 11 dígitos) ou CNPJ (12 a 14). */
const maskDocument = (value: string) => {
  const d = onlyDigits(value).slice(0, 14);
  if (d.length <= 11) {
    return d
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
  }
  return d
    .replace(/(\d{2})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1/$2")
    .replace(/(\d{4})(\d{1,2})$/, "$1-$2");
};

/** Cadastro de fornecedor ou cliente. O dígito verificador é conferido no backend. */
export default function CounterpartyFormModal({ token, counterparty, onClose, onSaved }: Props) {
  const [name, setName] = useState(counterparty?.name ?? "");
  const [document, setDocument] = useState(maskDocument(counterparty?.document ?? ""));
  const [email, setEmail] = useState(counterparty?.email ?? "");
  const [phone, setPhone] = useState(counterparty?.phone ?? "");
  const [pixKey, setPixKey] = useState(counterparty?.pixKey ?? "");
  const [notes, setNotes] = useState(counterparty?.notes ?? "");
  const [saving, setSaving] = useState(false);

  const digits = onlyDigits(document);
  const docError = digits && digits.length !== 11 && digits.length !== 14 ? "CPF tem 11 dígitos; CNPJ, 14." : "";

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    // Aberto do formulário de lançamento (por portal), o submit subiria na árvore
    // do React até o <form> de lá e o salvaria junto.
    e.stopPropagation();
    if (name.trim().length < 2) return toast.error("Informe o nome.");
    if (docError) return toast.error(docError);

    const payload = {
      name: name.trim(),
      document: digits || null,
      email: email.trim() || null,
      phone: phone.trim() || null,
      pixKey: pixKey.trim() || null,
      notes: notes.trim() || null,
    };

    setSaving(true);
    try {
      const saved = counterparty
        ? await updateCounterpartyApi(token, counterparty.id, payload)
        : await createCounterpartyApi(token, payload);
      toast.success("Cadastro salvo.");
      onSaved(saved);
    } catch (error: any) {
      toast.error(error?.message || "Erro ao salvar o cadastro.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="dpModalOverlay">
      <div className="dpModal" role="dialog" aria-modal="true" aria-label="Fornecedor ou cliente">
        <header className="dpModal__header">
          <h2 className="dpModal__title">{counterparty ? "Editar cadastro" : "Novo fornecedor ou cliente"}</h2>
          <button type="button" className="dpModal__close" onClick={onClose} disabled={saving} aria-label="Fechar">
            &times;
          </button>
        </header>
        <form onSubmit={save}>
          <div className="dpModal__body">
            <div className="dpFormGroup">
              <label htmlFor="cpName">Nome ou razão social *</label>
              <input
                id="cpName"
                className="dpInput"
                maxLength={160}
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={saving}
                autoFocus
              />
            </div>
            <div className="dpFormGrid">
              <div className="dpFormGroup">
                <label htmlFor="cpDoc">CPF ou CNPJ</label>
                <input
                  id="cpDoc"
                  className="dpInput"
                  inputMode="numeric"
                  value={document}
                  onChange={(e) => setDocument(maskDocument(e.target.value))}
                  disabled={saving}
                />
                {docError ? <span className="dpFormHint">{docError}</span> : null}
              </div>
              <div className="dpFormGroup">
                <label htmlFor="cpPix">Chave PIX</label>
                <input
                  id="cpPix"
                  className="dpInput"
                  maxLength={140}
                  value={pixKey}
                  onChange={(e) => setPixKey(e.target.value)}
                  disabled={saving}
                />
              </div>
              <div className="dpFormGroup">
                <label htmlFor="cpEmail">E-mail</label>
                <input
                  id="cpEmail"
                  type="email"
                  className="dpInput"
                  maxLength={160}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={saving}
                />
              </div>
              <div className="dpFormGroup">
                <label htmlFor="cpPhone">Telefone</label>
                <input
                  id="cpPhone"
                  className="dpInput"
                  maxLength={20}
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  disabled={saving}
                />
              </div>
            </div>
            <div className="dpFormGroup">
              <label htmlFor="cpNotes">Observações</label>
              <textarea
                id="cpNotes"
                className="dpTextarea"
                maxLength={1000}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                disabled={saving}
              />
            </div>
          </div>
          <footer className="dpModal__footer">
            <button type="button" className="dpBtn dpBtn--secondary" onClick={onClose} disabled={saving}>
              Cancelar
            </button>
            <button type="submit" className="dpBtn dpBtn--primary" disabled={saving}>
              {saving ? "Salvando..." : "Salvar"}
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
}
